import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Microphone, MagnifyingGlass, Spinner, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const MAX_MS = 15000;

export function HeroSearch() {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | listening | transcribing | thinking
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const stopTimerRef = useRef(null);

  useEffect(() => {
    api.get("/voice/config").then((r) => setVoiceEnabled(!!r.data.enabled)).catch(() => setVoiceEnabled(false));
    return () => clearTimeout(stopTimerRef.current);
  }, []);

  const goToExplore = ({ q = "", category = null, max_price = null } = {}) => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (category) params.set("category", category);
    if (max_price) params.set("max", String(max_price));
    navigate(`/explore?${params.toString()}`);
  };

  const submitTyped = (e) => {
    e.preventDefault();
    goToExplore({ q: text.trim() });
  };

  const stopRecording = () => {
    clearTimeout(stopTimerRef.current);
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  };

  const processAudio = async (blob) => {
    setStatus("transcribing");
    try {
      const fd = new FormData();
      fd.append("audio", blob, "voice.webm");
      const tr = await fetch(`${API}/voice/transcribe`, { method: "POST", body: fd, credentials: "include" });
      if (!tr.ok) {
        const body = await tr.json().catch(() => ({}));
        throw new Error(body.detail || "Transcription failed");
      }
      const { text: transcript } = await tr.json();
      setText(transcript || "");
      if (!transcript) { setStatus("idle"); toast("Didn't catch that — try again."); return; }

      setStatus("thinking");
      const { data } = await api.post("/voice/interpret", { text: transcript });
      goToExplore({ q: data.query || "", category: data.category, max_price: data.max_price });
    } catch (err) {
      toast.error(err.message || "Voice search failed. Please try again.");
    } finally {
      setStatus("idle");
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        processAudio(blob);
      };
      recorderRef.current = rec;
      rec.start();
      setStatus("listening");
      stopTimerRef.current = setTimeout(stopRecording, MAX_MS);
    } catch {
      toast.error("Microphone permission is needed for voice search.");
      setStatus("idle");
    }
  };

  const onMicClick = () => {
    if (!voiceEnabled) {
      toast("Voice search needs your Universal Key — add it in the Secrets tab to enable", { duration: 6000 });
      return;
    }
    if (status === "listening") stopRecording();
    else if (status === "idle") startRecording();
  };

  const busy = status === "transcribing" || status === "thinking";
  const statusLabel = status === "listening" ? "Listening… tap to stop"
    : status === "transcribing" ? "Transcribing your request…"
    : status === "thinking" ? "Finding your trips…" : "";

  return (
    <div className="w-full max-w-xl" data-testid="hero-search">
      <form onSubmit={submitTyped} className="flex items-center gap-2 rounded-full bg-white/95 backdrop-blur-xl border border-white/40 p-2 pl-5 shadow-xl focus-within:border-[#2E523A]/40 transition-colors duration-200">
        <MagnifyingGlass size={22} className="text-[#63675E] shrink-0" />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Try “warm beach trips in Asia under $2000”"
          className="flex-1 bg-transparent outline-none text-[15px] text-[#1A1C19] placeholder:text-[#8B8E85] py-2"
          data-testid="hero-search-input"
          disabled={busy}
        />
        <button
          type="button"
          onClick={onMicClick}
          aria-label="Voice search"
          data-testid="hero-mic-btn"
          className={`h-11 w-11 rounded-full flex items-center justify-center shrink-0 transition-colors duration-200 ${
            status === "listening" ? "bg-[#E8B291] text-[#1A1C19] animate-pulse"
              : busy ? "bg-[#EAEBE6] text-[#63675E]"
              : voiceEnabled ? "bg-[#2E523A] text-white hover:bg-[#26442f]"
              : "bg-[#EAEBE6] text-[#8B8E85]"
          }`}
          disabled={busy}
          title={voiceEnabled ? "Speak your travel request" : "Voice search needs your Universal Key"}
        >
          {busy ? <Spinner size={20} className="animate-spin" /> : <Microphone size={20} weight="duotone" />}
        </button>
        <button type="submit" className="h-11 w-11 rounded-full bg-[#1A1C19] text-white flex items-center justify-center shrink-0 hover:bg-[#2E523A] transition-colors duration-200" data-testid="hero-search-submit" aria-label="Search">
          <ArrowRight size={20} weight="bold" />
        </button>
      </form>
      <div className="h-6 mt-2 pl-2">
        {statusLabel && (
          <span className="inline-flex items-center gap-2 text-sm text-white/90" data-testid="hero-search-status">
            {status === "listening" && <span className="h-2.5 w-2.5 rounded-full bg-[#E8B291] animate-pulse" />}
            {statusLabel}
          </span>
        )}
        {!voiceEnabled && !statusLabel && (
          <span className="text-xs text-white/70" data-testid="hero-voice-hint">
            Type and press enter, or add your Universal Key in Secrets to enable voice search.
          </span>
        )}
      </div>
    </div>
  );
}
